<template>
  <div class="claim-status-update-item">
    <div v-if="baggageItem">
      <h2 class="subtitle">{{ getBaggageItemDescription(baggageItem) }}</h2>

      <div v-if="isItemMissingData(baggageItem)">
        <ms-image-upload-wrapper
          class="image_upload"
          v-if="!baggageItem.wholeCaseImage || baggageItem.wholeCaseImage.imageUrl == null"
          :showControls="updateImage.wholeCase !== undefined"
          :defaultImageSrc="wholeCaseUpload.defaultImageSrc"
          :imageUrl="getWholeCaseImage()"
          :caption="wholeCaseUpload.caption"
          :uploadFolder="guid"
          @imageUploadSuccess="onWholeCaseUploadSuccess(wholeCaseUpload, ...arguments)"
          @imageDeleteClick="onWholeCaseUploadDelete"
        />

        <ms-image-upload-wrapper
          class="image_upload"
          v-if="!baggageItem.badgeImage || baggageItem.badgeImage.imageUrl == null"
          :showControls="updateImage.badgeImage !== undefined"
          :defaultImageSrc="badgeUpload.defaultImageSrc"
          :imageUrl="getBadgeImage()"
          :caption="badgeUpload.caption"
          :uploadFolder="guid"
          @imageUploadSuccess="onBadgeUploadSuccess(badgeUpload, ...arguments)"
          @imageDeleteClick="onBadgeUploadDelete"
        />

        <ms-image-upload-wrapper
          class="image_upload"
          v-if="!baggageItem.baggageTagImage || baggageItem.baggageTagImage.imageUrl == null"
          :showControls="updateImage.baggageTagImage !== undefined"
          :defaultImageSrc="baggageTagUpload.defaultImageSrc"
          :imageUrl="getBaggageTagImage()"
          :caption="baggageTagUpload.caption"
          :uploadFolder="guid"
          @imageUploadSuccess="onBaggageTagUploadSuccess(baggageTagUpload, ...arguments)"
          @imageDeleteClick="onBaggageTagUploadDelete"
        />

        <!-- Area of damage image upload -->
        <div v-if="baggageItem.areaOfDamageImages.length === 0">
          <ms-image-upload-wrapper
            class="image_upload"
            v-for="(upload, index) in updateImage.areaOfDamageImages"
            :key="`area-of-damage-${index}`"
            :defaultImageSrc="upload.defaultImageSrc"
            :imageUrl="getAreaOfDamageImage(upload)"
            :file="upload.file"
            :showControls="updateImage.areaOfDamageImages.length > 0"
            :caption="upload.caption"
            :uploadFolder="guid"
            :showSelect="upload.showSelect"
            :selectPlaceholder="upload.selectPlaceholder"
            :selectDisplayKey="upload.selectDisplayKey"
            :selectVal.sync="upload.selectVal"
            :selectOptions="upload.selectOptions"
            @imageUploadSuccess="onDamagedAreaUploadSuccess(index, ...arguments)"
            @imageDeleteClick="onAreaOfDamageImageDelete(upload, index)"
          />
        </div>

        <!-- More photos button -->
        <ms-file-input-button
          v-if="moreUploadsButton.show"
          class="more_uploads_button"
          :button="moreUploadsButton"
          @change="onMoreUploadsChange"/>
      </div>

      <div v-else>
        <div class="claim-status">
          <div class="claim-status-message">
            {{ $t(`${pagei18nKey}.itemNoMissingData`) }}
          </div>
        </div>
      </div>
    </div>

    <div v-else>
      <div class="loading-div" v-if="loading">
        <img class="spinner" :src="loadingSpinner"/>
      </div>
    </div>

    <!-- Navigation buttons -->
    <div class="update_item_button__container">
      <ms-navigation-bar
        :buttons="[backButton, updateClaimButton]"
        @navigationButtonClick="navigationButtonClick"
      />
    </div>
  </div>
</template>

<script>
import api from '@/api/api'
import AppSettings from '@/app-settings.json'
import AppButton, {ButtonStates, ButtonTypes} from '@/components/app-button'
import MsImageUploadWrapper from '@/components/ms-image-upload-wrapper'
import MsFileInputButton from '@/components/ms-file-input-button'
import MsNavigationBar, {DefaultNavigationButtons} from '@/components/ms-navigation-bar'
import {AreaOfDamageImage} from '@/model/areaOfDamageImage'
import {ImageMeta} from '@/model/imageMeta'
import ImageUpdate from '@/model/imageUpdate'

export default {
  name: 'ClaimStatusUpdateItemView',
  components: {
    AppButton,
    MsNavigationBar,
    MsImageUploadWrapper,
    MsFileInputButton
  },
  data () {
    return {
      loadingSpinner: AppSettings.submitInProgress,
      loading: false,
      wholeCaseUpload: {
        caption: this.$t('message.baggageItem.wholeCaseUpload'),
        defaultImageSrc: '/static/theme/img/whole-case-photo.png'
      },
      badgeUpload: {
        caption: this.$t('message.baggageItem.badgeUpload'),
        defaultImageSrc: '/static/theme/img/brand-badge-photo.png'
      },
      baggageTagUpload: {
        caption: this.$t('message.baggageItem.baggageTagUpload'),
        defaultImageSrc: '/static/theme/img/baggage-tag-photo.png'
      },
      areaOfDamageUpload: {
        caption: this.$t('message.baggageItem.areaOfDamageUpload.caption'),
        defaultImageSrc: '/static/theme/img/area-of-damage-photo.png',
        selectPlaceholder: this.$t('message.baggageItem.areaOfDamageUpload.selectPlaceholder'),
        selectDisplayKey: 'option',
        selectOptions: [
          {
            option: this.$t('message.baggageItem.areaOfDamageUpload.selectOptions.top')
          },
          {
            option: this.$t('message.baggageItem.areaOfDamageUpload.selectOptions.bottom')
          },
          {
            option: this.$t('message.baggageItem.areaOfDamageUpload.selectOptions.left')
          },
          {
            option: this.$t('message.baggageItem.areaOfDamageUpload.selectOptions.right')
          }
        ],
        selectVal: null,
        showSelect: false
      },
      moreUploadsButton: {
        show: false,
        state: ButtonStates.ENABLED,
        type: ButtonTypes.QUINARY,
        hasIconLeft: true,
        text: 'message.baggageItem.morePhotos'
      },
      pagei18nKey: 'message.claimStatus',
      baggageItem: null,
      isFormValid: false,
      updateImage: {
        areaOfDamageImages: []
      },
      types: [],
      materials: [],
      brands: []
    }
  },
  props: {
    guid: String,
    index: [Number, String]
  },
  /**
   * Check whether we have route props and if they match a given object in
   * item array then set relevant props to pass through to form components
   */
  mounted () {
    this.loading = true
    api.getClaimStatus(this.guid)
      .then(response => {
        this.loading = false
        this.baggageItem = response.data.baggageItems[this.index] || null

        // If the baggage item in progress (repair or replace route) forward to status page
        if (!response.data.requireMissingBaggageInformation) {
          this.$router.push(`/claim-status/${this.guid}`)
        }

        /**
         * Get baggage types data from api
         */
        api.getBaggageTypes()
          .then(response => {
            this.types = response.data
          })
          .catch(console.error)

        /**
         * Get baggage brands data from api
         */
        api.getBaggageBrands()
          .then(response => {
            this.brands = response.data
          })
          .catch(console.error)

        /**
        * Get baggage materials data from api
        */
        api.getBaggageMaterials()
          .then(response => {
            this.materials = response.data
          })
          .catch(console.error)
      })
      .catch(error => console.error(error))

    this.updateImage.areaOfDamageImages.push({ ...this.areaOfDamageUpload })
  },
  computed: {
    /**
     * Get add to claim button object
     */
    updateClaimButton: {
      get () {
        let defaultNextButton = JSON.parse(JSON.stringify(DefaultNavigationButtons.NEXT))
        return Object.assign(
          defaultNextButton,
          {
            state: this.isFormValid ? ButtonStates.ENABLED : ButtonStates.DISABLED,
            text: `${this.pagei18nKey}.updateClaimButton`
          }
        )
      }
    },

    /**
     * Get back button object
     */
    backButton: {
      get () {
        let defaultBackButton = JSON.parse(JSON.stringify(DefaultNavigationButtons.BACK))
        return Object.assign(
          defaultBackButton,
          {
            state: ButtonStates.ENABLED
          }
        )
      }
    }
  },
  methods: {

    isItemMissingData (baggageItem) {
      return !((baggageItem.wholeCaseImage && baggageItem.wholeCaseImage.imageUrl) &&
                (baggageItem.badgeImage && baggageItem.badgeImage.imageUrl) &&
                // && baggageItem.baggageTagImage???
                baggageItem.areaOfDamageImages.length > 0)
    },

    getBaggageItemDescription (baggageItem) {
      let baggageBrand = this.brands.find(x => x.id === baggageItem.brand.globalId)
      let baggageType = this.types.find(x => x.id === baggageItem.type.globalId)
      let baggageMaterial = this.materials.find(x => x.id === baggageItem.material.globalId)

      if (baggageBrand && baggageType && baggageMaterial) {
        let brandName = baggageItem.brand.additionalValue || baggageBrand.name
        let typeName = baggageItem.type.additionalValue || baggageType.name
        let materialName = baggageItem.material.additionalValue || baggageMaterial.name
        return `${brandName} | ${typeName} | ${materialName}`
      }
      return ''
    },

    convertToAreaOfDamageImages (areaOfDamageImages) {
      let images = []
      areaOfDamageImages.forEach(image => {
        if (image.imageUrl) {
          images.push(new AreaOfDamageImage({
            imageUrl: image.imageUrl,
            selectVal: image.selectVal
          }))
        }
      })
      return images
    },

    /**
     * On navigation button click
     *
     * @param {string} buttonId - Id of button
     */
    navigationButtonClick (buttonId) {
      if (buttonId === DefaultNavigationButtons.BACK.id) {
        this.$router.go(-1)
      } else if (buttonId === DefaultNavigationButtons.NEXT.id) {
        // Convert images to objects
        let options = {
          wholeCaseImage: null,
          badgeImage: null,
          baggageTagImage: null,
          areaOfDamageImages: null
        }

        if (this.updateImage.wholeCase) {
          options.wholeCaseImage = new ImageMeta({
            imageUrl: this.updateImage.wholeCase.imageUrl
          })
        }

        if (this.updateImage.badgeImage) {
          options.badgeImage = new ImageMeta({
            imageUrl: this.updateImage.badgeImage.imageUrl
          })
        }

        if (this.updateImage.baggageTagImage) {
          options.baggageTagImage = new ImageMeta({
            imageUrl: this.updateImage.baggageTagImage.imageUrl
          })
        }

        if (this.updateImage.areaOfDamageImages[0].imageUrl) {
          options.areaOfDamageImages = this.convertToAreaOfDamageImages(this.updateImage.areaOfDamageImages)
        }

        let updateObject = new ImageUpdate(options)

        api.postClaimImageUpdate(this.guid, updateObject, this.baggageItem.id)
          .then(result => {
            this.$router.push(`/claim-status/${this.guid}`)
          })
          .catch(error => console.error(error))
      }
    },

    /**
     * Add new area of damage uploads for each file in event
     *
     * @param {Event} event - the input file change event
     */
    onMoreUploadsChange (event) {
      let newUploads = []
      const files = Array.from(event.target.files)

      files.forEach((file) => {
        newUploads.push(
          Object.assign(
            JSON.parse(JSON.stringify(this.areaOfDamageUpload)),
            {
              file: file
            }
          )
        )
      })

      this.updateImage.areaOfDamageImages.push(...newUploads)
    },

    /**
     * Get an area of damage upload's image url
     *
     * @param {string} upload - The upload object to get image url for
     */
    getAreaOfDamageImage (upload) {
      return upload.imageUrl ? upload.imageUrl : null
    },

    /**
     * On damaged area image upload success push the relevant details
     * into the current upload item to be pushed into images array on submit
     *
     * @param {number} index - Index of upload object in areaOfDamageUploads array
     * @param {string} url - Url of uploaded image
     */
    onDamagedAreaUploadSuccess (index, url) {
      // Show the select options
      this.updateImage.areaOfDamageImages[index].showSelect = true

      // Format the data we need to pass to the store later on
      this.updateImage.areaOfDamageImages[index].imageUrl = url

      // If the more uploads button isn't already showing then show it
      if (!this.moreUploadsButton.show) {
        this.moreUploadsButton.show = true
      }

      // After initial upload, set file to null to avoid additional initiliasation on edit
      this.updateImage.areaOfDamageImages[index].file = null

      this.imageValidityCheck()
    },

    /**
     * On area of damage image delete click, reset uploader field or delete
     * item entirely if it is not the first item
     *
     * @param {Object} upload - The upload object for the deleted image item
     * @param {number} index - The index of the image in areaOfDamageImages array
     */
    onAreaOfDamageImageDelete (upload, index) {
      if (index > 0 || (index === 0 && this.updateImage.areaOfDamageImages.length > 1)) {
        this.updateImage.areaOfDamageImages.splice(index, 1)
      } else {
        upload.imageUrl = null
        upload.showSelect = false
        upload.showControls = false
        upload.selectVal = null
      }

      this.moreUploadsButton.show = this.updateImage.areaOfDamageImages[0].imageUrl

      this.imageValidityCheck()
    },

    /**
     * Get the whole case upload image url
     */
    getWholeCaseImage () {
      if (this.updateImage.wholeCase) {
        let storedWholeCaseImage = this.updateImage.wholeCase
        if (storedWholeCaseImage && storedWholeCaseImage.imageUrl) {
          return storedWholeCaseImage.imageUrl
        }
        return null
      }
    },

    /**
     * Remove the image from the whole case upload
     */
    onWholeCaseUploadDelete () {
      this.updateImage.wholeCase.imageUrl = null
      this.imageValidityCheck()
    },

    /**
     * on image upload success for individual upload fields
     * push image object into the item's images
     *
     * @param {string} url - Url of uploaded image
     */
    onWholeCaseUploadSuccess (upload, url) {
      this.updateImage.wholeCase = Object.assign(upload, { 'imageUrl': url })
      this.imageValidityCheck()
    },

    /**
     * Get the badge upload image url
     */
    getBadgeImage () {
      if (this.currentItem) {
        let storedBadgeImage = this.updateImage.badgeImage
        if (storedBadgeImage && storedBadgeImage.imageUrl) {
          return storedBadgeImage.imageUrl
        }
        return null
      }
    },

    /**
     * Remove the image from the badge upload
     */
    onBadgeUploadDelete () {
      this.updateImage.badgeImage.imageUrl = null
      this.imageValidityCheck()
    },

    /**
     * on image upload success for individual upload fields
     * push image object into the item's images
     *
     * @param {string} url - Url of uploaded image
     */
    onBadgeUploadSuccess (upload, url) {
      this.updateImage.badgeImage = Object.assign(upload, { 'imageUrl': url })
      this.imageValidityCheck()
    },

    /**
     * Get the baggage tag upload image url
     */
    getBaggageTagImage () {
      if (this.currentItem) {
        let storedBaggageTagImage = this.updateImage.baggageTagImage
        if (storedBaggageTagImage && storedBaggageTagImage.imageUrl) {
          return storedBaggageTagImage.imageUrl
        }
        return null
      }
    },

    /**
     * Remove the image from the baggage tag upload
     */
    onBaggageTagUploadDelete () {
      this.updateImage.baggageTagImage.imageUrl = null
      this.imageValidityCheck()
    },

    /**
     * on image upload success for individual upload fields
     * push image object into the item's images
     *
     * @param {string} url - Url of uploaded image
     */
    onBaggageTagUploadSuccess (upload, url) {
      this.updateImage.baggageTagImage = Object.assign(upload, { 'imageUrl': url })
      this.imageValidityCheck()
    },

    /**
     * Check whether the minimum images are uploaded and fire an emit
     * with valid value
     */
    imageValidityCheck () {
      this.isFormValid = false
      // If whole case image was updated
      if (this.updateImage.wholeCase ||
          this.updateImage.badgeImage ||
          // this.updateImage.baggageTagImage || // optional I think
          this.updateImage.areaOfDamageImages[0].imageUrl
      ) {
        this.isFormValid = true
      } else {
        this.isFormValid = false
      }
      return this.isFormValid
    }
  }
}
</script>

<style lang="scss">
@import "../../assets/sass/main.scss";

.claim-status {
  padding: $dims-padding-default;
  border: 1px solid #dddddd;
  background: $secondary-brand-color;
}

.claim-status-update-item {
  @extend %app_form_container;
}

.update_item_button__container {
  margin-top: $dims-spacing-xlarge;
}

.image_upload {
  margin-bottom: $dims-spacing-small;
}

.loading-div {
  position: relative;
  height: 100px;

  .spinner {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }
}
</style>
